import React from 'react'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

import { Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useScreenSize } from '../../../common'
import { EmptyStateFooterComponent } from '../EmptyStateFooterComponent'
import { InvestmentsData } from './InvestmentsAllocationChart'
import { currencyDisplay, percentageDisplay } from './util/createInvestementsAllocationData'

interface InvestmentAllocationListProps {
  data: InvestmentsData
  onSelected: (id: string) => void
  selectedId?: string
  selectedTabs: number
  setSelectedTabs: (value: number) => void
}

export const InvestementAllocationList = ({
  data,
  onSelected,
  selectedId,
  selectedTabs,
  setSelectedTabs,
}: InvestmentAllocationListProps) => {
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue !== null) {
      setSelectedTabs(newValue)
    }
  }
  const { isMobile } = useScreenSize()
  const {
    investments: { allocation: copy },
  } = useGlobalCopyStore()

  const toggleButtonsList = [
    { label: copy.tabs_label_dollar, value: 0 },
    { label: copy.tabs_label_percent, value: 1 },
  ]
  const isEmptyState = !data?.investmentsData.length
  const { isSmallMobile } = useScreenSize()

  return (
    <Stack sx={{ mt: 15, gap: 12, width: isMobile ? 250 : 412 }}>
      <ToggleButtonGroup
        exclusive={true}
        fullWidth={true}
        onChange={handleChange}
        value={isEmptyState ? -1 : selectedTabs}
      >
        {toggleButtonsList.map(({ label, value }) => (
          <ToggleButton color="primary" key={label} value={value}>
            <Text bold={true} textTransform={'capitalize'} variant="body1">
              {label}
            </Text>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <List sx={{ px: 12 }}>
        {data.investmentsData.map((item) => (
          <React.Fragment key={item.guid}>
            <ListItem
              disableGutters={true}
              disablePadding={true}
              sx={{
                minHeight: 44,
              }}
            >
              <ListItemButton
                aria-expanded={Boolean(selectedId === item.guid)}
                onClick={() => onSelected(item.guid)}
                sx={{ minHeight: 44, px: 12, py: 0 }}
              >
                <Box
                  borderRadius={4}
                  flexShrink={0}
                  height={8}
                  sx={{ backgroundColor: item.color, mr: 8 }}
                  width={8}
                />
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Text bold={selectedId === item.guid}>{item.displayName}</Text>
                  <Text bold={selectedId === item.guid} variant="body1">
                    {selectedTabs === 0
                      ? currencyDisplay(item.value)
                      : percentageDisplay(item.percentage)}
                  </Text>
                </Stack>
              </ListItemButton>
            </ListItem>

            <Divider />

            {item.holdings && item.holdings.length > 0 && (
              <Collapse in={Boolean(selectedId === item.guid)} unmountOnExit={true}>
                <List>
                  {item?.holdings?.map((subItem) => (
                    <ListItem
                      key={subItem.displayName}
                      sx={{
                        pl: 24,
                        pr: 8,
                        py: 0,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" width="100%">
                        <Text>{subItem.displayName} </Text>
                        <Text>
                          {selectedTabs === 0
                            ? currencyDisplay(subItem.value)
                            : percentageDisplay(subItem.percentage)}
                        </Text>
                      </Stack>
                    </ListItem>
                  ))}
                  <Divider />
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      {isEmptyState ? (
        <>
          <Text sx={{ color: 'secondary.main', p: 8, textAlign: 'center' }}>{copy.empty_data}</Text>
          <Divider />
          <EmptyStateFooterComponent
            sx={{
              '.inner-container': { width: isSmallMobile ? '100%' : 364 },
            }}
          />
        </>
      ) : (
        selectedTabs === 0 && (
          <Text bold={true} sx={{ textAlign: 'right', pr: 24 }}>
            {`${copy.total_label_text} ${currencyDisplay(data.totalAmount)}`}
          </Text>
        )
      )}
    </Stack>
  )
}
